import { Combobox as HeadlessCombobox } from '@headlessui/react';
import React, { ComponentPropsWithoutRef, memo, ReactElement, useCallback } from 'react';
import {
  Control,
  FieldPathByValue,
  FieldValues,
  PathValue,
  useController,
  useWatch,
} from 'react-hook-form';

export type FormComboboxProps<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
> = {
  control: Control<TFieldValues>;
  defaultValue?: PathValue<TFieldValues, TPath>;
  name: TPath;
  onSelect?: (value: PathValue<TFieldValues, TPath>) => void;
} & Omit<
  ComponentPropsWithoutRef<typeof HeadlessCombobox>,
  'defaultValue' | 'onBlur' | 'onChange' | 'onSelect' | 'value'
>;

const comboboxValueById = (a: unknown, b: unknown): boolean => {
  if (
    a !== null &&
    b !== null &&
    typeof a === 'object' &&
    typeof b === 'object' &&
    'id' in a &&
    'id' in b
  ) {
    return a.id === b.id;
  }

  return a === b;
};

const FormCombobox = <
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
>({
  control,
  defaultValue,
  onSelect,
  name,
  ...props
}: FormComboboxProps<TFieldValues, TPath>): null | ReactElement => {
  const { field } = useController({
    control,
    defaultValue,
    name,
  });
  const latestValue = useWatch({
    control,
    name,
    defaultValue,
  });

  const handleChange = useCallback(
    (value: PathValue<TFieldValues, TPath>) => {
      field.onChange(value);
      onSelect?.(value);
    },
    [field, onSelect],
  );
  return (
    <HeadlessCombobox
      as={'div'}
      by={comboboxValueById}
      {...props}
      key={field.name}
      name={field.name}
      onBlur={field.onBlur}
      onChange={handleChange}
      ref={field.ref}
      value={(latestValue || '') as unknown}
    >
      {props.children}
    </HeadlessCombobox>
  );
};

export default memo(FormCombobox) as typeof FormCombobox;
